import React, { FC, JSX, useEffect, useState } from "react";
import "./ModalAccessProfileLevelInsert.scss";
import ModalDefault from "../../../../components/ModalDefault/ModalDefault";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth } from "../../../../providers/AuthProvider";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Accordion, Card, Col, FormGroup, Row } from "react-bootstrap";
import ErrorMessage from "../../../../components/ErrorMessage/ErrorMessage";
import { schema } from "./Validate";
import { useAccessProfile } from "../../../../providers/Access/AccessProfile/AccessProfileProvider";
import { AccessPageModel } from "../../../../models/Access/AccessPage/AccessPageModel";
import SkeletonTable from "../../../SkeletonTable/SkeletonTable";
import { usePaginate } from "../../../../providers/PaginateProvider";
import { BvIcon } from "bevi-icon";

interface ModalAccessProfileLevelInsertProps {
  show: boolean;
  onClose: () => void;
  accessProfileId: number;
}

const ModalAccessProfileLevelInsert: FC<ModalAccessProfileLevelInsertProps> = ({
  show,
  onClose,
  accessProfileId,
}) => {
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [isChildrenPages, setIsChildrenPages] = useState<boolean>(false);
  const [expandedRow, setExpandedRow] = useState<number>();
  const [pagesAccess, setPagesAccess] = useState<any[]>([]);
  const {
    handleListPages,
    accessPages,
    isLoadingModalAccess,
    menu,
    setError,
    handlePermission,
    handleMenuList,
  } = useAccessProfile();

  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const reset = () => {
    setValue("access_profile_id", null);
    setPagesAccess([]);
    setExpandedRow(0);
  };

  useEffect(() => {
    if (show) {
      if (accessProfileId > 0) {
        setValue("access_profile_id", accessProfileId);
        handleMenuList(accessProfileId);
      } else {
        reset();
      }
      handleListPages({ withPaginate: false });
    }
  }, [show]);

  useEffect(() => {
    if (menu) {
      const pagesAccessProfileId: any[] = [];

      menu.forEach((element: AccessPageModel) => {
        if (element?.children && element?.children?.length > 0) {
          element?.children?.forEach((x: AccessPageModel) => {
            if (pagesAccessProfileId.includes(x?.id)) {
              const index = pagesAccessProfileId.indexOf(x?.id);
              pagesAccessProfileId.slice(index, 1);
            } else {
              pagesAccessProfileId.push(x?.id);
            }
          });
        }
        if (pagesAccessProfileId.includes(element?.id)) {
          const index = pagesAccessProfileId.indexOf(element?.id);
          pagesAccessProfileId.splice(index, 1);
        } else {
          pagesAccessProfileId.push(element?.id);
        }
      });

      setPagesAccess(pagesAccessProfileId);
    }
  }, [menu]);

  const handleClose = () => {
    setError("");
    reset();
    onClose();
  };

  const onSubmit = async (data: any) => {
    if (!data && pagesAccess.length <= 0) return;

    setIsLoadingSubmit(true);
    setError("");

    const permission = {
      access_profile_id: data?.access_profile_id,
      access_pages: pagesAccess,
    };

    const ret = await handlePermission(permission);

    if (ret) {
      toast.success("Acessos atribuídos com sucesso!");
      handleClose();
    }

    setIsLoadingSubmit(false);
  };

  const handleCheckedPages = (id?: number) => {
    if (!id) return;

    setPagesAccess((prev) => {
      if (prev.includes(id)) {
        return prev.filter((pageId) => pageId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const renderPagesChildren = (item: AccessPageModel): JSX.Element => {
    return (
      <tr>
        <td colSpan={4}>
          <div className="p-3 bg-light rounded">
            <Card>
              <table className="table table-flush m-0 w-100">
                <thead className="thead-light">
                  <tr>
                    <th className="text-uppercase opacity-7 text-center w-10"></th>
                    <th className="text-uppercase opacity-7 w-45">Nome</th>
                    <th className="text-uppercase opacity-7 w-45">URL</th>
                  </tr>
                </thead>
                <tbody>
                  {item?.children && item?.children?.length > 0 ? (
                    <>
                      {item?.children?.map((x: AccessPageModel) => (
                        <tr key={x.id}>
                          <td className="text-center">
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id={x?.name}
                                name={x?.name}
                                checked={pagesAccess.includes(x?.id)}
                                onChange={() => handleCheckedPages(x?.id)}
                              />
                            </div>
                          </td>
                          <td className="text-sm">{x?.name}</td>
                          <td className="text-sm">{x?.layout}</td>
                        </tr>
                      ))}
                    </>
                  ) : null}
                </tbody>
              </table>
            </Card>
          </div>
        </td>
      </tr>
    );
  };

  const renderList = (): JSX.Element => {
    return (
      <>
        {accessPages.length > 0 ? (
          <>
            {accessPages.map((item: AccessPageModel) => {
              if (item?.children && item?.children?.length > 0) {
                return (
                  <React.Fragment key={item.id}>
                    <tr>
                      <td>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id={item?.name}
                            name={item?.name}
                            checked={pagesAccess.includes(item?.id)}
                            onChange={() => handleCheckedPages(item?.id)}
                          />
                        </div>
                      </td>
                      <td className="text-sm">{item.name}</td>
                      <td className="text-sm">{`/${item.path_name}`}</td>
                      <td className="text-end">
                        <a
                          onClick={() =>
                            setExpandedRow(
                              expandedRow === item.id ? 0 : item.id
                            )
                          }
                        >
                          <BvIcon
                            name={
                              expandedRow === item.id
                                ? "chevronUp"
                                : "chevronDown"
                            }
                            weight={600}
                            width={25}
                            height={25}
                          />
                        </a>
                      </td>
                    </tr>
                    {expandedRow === item.id && renderPagesChildren(item)}
                  </React.Fragment>
                );
              } else {
                return (
                  <React.Fragment key={item.id}>
                    <tr>
                      <td>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id={item?.name}
                            name={item?.name}
                            checked={pagesAccess.includes(item?.id)}
                            onChange={() => handleCheckedPages(item?.id)}
                          />
                        </div>
                      </td>
                      <td className="text-sm">{item.name}</td>
                      <td className="text-sm">{`/${item.path_name}`}</td>
                      <td className="text-end"></td>
                    </tr>
                  </React.Fragment>
                );
              }
            })}
          </>
        ) : (
          <tr>
            <td colSpan={4} className="text-center text-sm">
              Não há registros
            </td>
          </tr>
        )}
      </>
    );
  };

  const renderContent = (): JSX.Element => {
    return (
      <>
        {!isLoadingModalAccess ? (
          <Row>
            <Col md={12} className="mb-3">
              Páginas
            </Col>
            <Col md={12}>
              <Card>
                <div className="table-responsive">
                  <table className="table table-flush m-0 w-100">
                    <thead className="thead-light">
                      <tr>
                        <th className="text-uppercase opacity-7 text-center w-10"></th>
                        <th className="text-uppercase opacity-7 w-45">Nome</th>
                        <th className="text-uppercase opacity-7 w-45">URL</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>{renderList()}</tbody>
                  </table>
                </div>
              </Card>
            </Col>
          </Row>
        ) : (
          <SkeletonTable />
        )}
      </>
    );
  };

  return (
    <ModalDefault
      title={"Editar atribuições de acesso"}
      show={show}
      icon="article"
      onClose={handleClose}
      sizeModal={"xl"}
      showFooter={true}
      buttonText={isLoadingSubmit ? "Salvando..." : "Salvar"}
      showButtonSpinner={isLoadingSubmit}
      disabledSubmit={isLoadingSubmit}
      handleSubmit={handleSubmit(onSubmit)}
      backdrop="static"
      backgroundColor="#f8f9fa"
    >
      <div
        className="ModalAccessProfileLevelInsert"
        data-testid="ModalAccessProfileLevelInsert"
      >
        {renderContent()}
      </div>
    </ModalDefault>
  );
};

export default ModalAccessProfileLevelInsert;
